import { foldDiacritics } from "@/lib/tool-kit/ciphers/alphabet";

/**
 * The telephone keypad as a cipher: each letter becomes the key it sits on, optionally
 * with how many presses reach it.
 *
 * The letters follow ITU-T E.161: 2 ABC, 3 DEF, 4 GHI, 5 JKL, 6 MNO, 7 PQRS, 8 TUV,
 * 9 WXYZ. Keys 1 and 0 carry no letters.
 *
 * - **Multi-tap** writes a letter as its key repeated: C is 222, S is 7777.
 * - **Key and press** writes the same thing as a pair: C is 2-3.
 * - **T9 digits** writes one digit per letter, so 4663 is "good", "home", "gone" and
 *   "hood" alike; decoding needs a dictionary and returns several candidates.
 */

export type KeypadMethod = "multitap" | "pairs" | "t9";
export type WordBreak = "0" | "/";

export const KEYPAD: Readonly<Record<string, string>> = {
  "2": "ABC",
  "3": "DEF",
  "4": "GHI",
  "5": "JKL",
  "6": "MNO",
  "7": "PQRS",
  "8": "TUV",
  "9": "WXYZ",
};

/** Every key of the keypad in dialling order, with its letters ("" for 1 and 0). */
export const KEYPAD_KEYS: readonly { key: string; letters: string }[] = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
].map((key) => ({ key, letters: KEYPAD[key] ?? "" }));

const LETTER_KEYS = new Map<string, { key: string; presses: number }>(
  Object.entries(KEYPAD).flatMap(([key, letters]) =>
    [...letters].map(
      (letter, index) => [letter, { key, presses: index + 1 }] as const
    )
  )
);

const WHITESPACE = /\s+/;
const IS_SPACE = /\s/;
const MULTITAP_TOKEN = /^([2-9])\1*$/;
const PAIR_TOKEN = /^([2-9])\D+(\d+)$/;
const BREAK_TOKENS = new Set(["0", "/", "|"]);
const T9_SPLIT = /[\s/|0]+/;
const T9_DIGITS = /^[2-9]+$/;
const SIGN = /[a-z]/;

export interface KeypadRow {
  letter: string;
  key: string;
  presses: number;
  /** The letter as it is written in the chosen method. */
  code: string;
}

export interface KeypadEncoding {
  output: string;
  /** One row per letter, in order. */
  rows: KeypadRow[];
  /** Characters that are neither letters nor spaces, each listed once. */
  ignored: string[];
}

/** The key and press count of a letter, or null for anything that is not A–Z. */
export function letterKey(
  letter: string
): { key: string; presses: number } | null {
  return LETTER_KEYS.get(letter.toUpperCase()) ?? null;
}

function rowFor(letter: string, method: KeypadMethod): KeypadRow | null {
  const found = letterKey(letter);
  if (!found) {
    return null;
  }
  let code = found.key.repeat(found.presses);
  if (method === "pairs") {
    code = `${found.key}-${found.presses}`;
  } else if (method === "t9") {
    code = found.key;
  }
  return { letter: letter.toUpperCase(), ...found, code };
}

/** Split text into words of key rows; spaces end a word, other symbols are ignored. */
function wordsOfRows(
  text: string,
  method: KeypadMethod
): { words: KeypadRow[][]; ignored: string[] } {
  const words: KeypadRow[][] = [[]];
  const ignored = new Set<string>();
  for (const char of foldDiacritics(text)) {
    const row = rowFor(char, method);
    if (row) {
      words.at(-1)?.push(row);
    } else if (IS_SPACE.test(char)) {
      if ((words.at(-1)?.length ?? 0) > 0) {
        words.push([]);
      }
    } else {
      ignored.add(char);
    }
  }
  return {
    words: words.filter((word) => word.length > 0),
    ignored: [...ignored],
  };
}

export function encodeKeypad(
  text: string,
  method: KeypadMethod,
  wordBreak: WordBreak
): KeypadEncoding {
  const { words, ignored } = wordsOfRows(text, method);
  const joiner = method === "t9" ? "" : " ";
  const output = words
    .map((word) => word.map((row) => row.code).join(joiner))
    .join(` ${wordBreak} `);
  return { output, rows: words.flat(), ignored };
}

export interface KeypadToken {
  token: string;
  /** The letter it stands for; null for an invalid token. */
  letter: string | null;
  isBreak: boolean;
}

export interface KeypadDecoding {
  output: string;
  tokens: KeypadToken[];
  invalid: string[];
}

function decodeToken(token: string, method: KeypadMethod): string | null {
  const match = (method === "pairs" ? PAIR_TOKEN : MULTITAP_TOKEN).exec(token);
  if (!match) {
    return null;
  }
  const presses = method === "pairs" ? Number(match[2]) : token.length;
  return KEYPAD[match[1]][presses - 1] ?? null;
}

/** Decode multi-tap or key-and-press text. T9 digits go through `t9Words` instead. */
export function decodeKeypad(
  input: string,
  method: KeypadMethod
): KeypadDecoding {
  const tokens: KeypadToken[] = [];
  const invalid: string[] = [];
  let output = "";
  for (const token of input.trim().split(WHITESPACE)) {
    if (token === "") {
      continue;
    }
    if (BREAK_TOKENS.has(token)) {
      tokens.push({ token, letter: null, isBreak: true });
      output += " ";
      continue;
    }
    const letter = decodeToken(token, method);
    tokens.push({ token, letter, isBreak: false });
    if (letter === null) {
      invalid.push(token);
      output += "?";
    } else {
      output += letter;
    }
  }
  return { output, tokens, invalid };
}

/* ---------------------------------------------------------------------- T9 */

/** The digits a word is typed with: "good" → "4663". Null if it has a non-letter. */
export function t9Code(word: string): string | null {
  let code = "";
  for (const char of word) {
    const found = letterKey(char);
    if (!(found && SIGN.test(char.toLowerCase()))) {
      return null;
    }
    code += found.key;
  }
  return code;
}

/**
 * The words of a T9 digit string. Spaces, 0, / and | separate words; anything that is not
 * 2–9 is returned in `invalid`.
 */
export function t9Words(input: string): { words: string[]; invalid: string[] } {
  const words: string[] = [];
  const invalid: string[] = [];
  for (const piece of input.split(T9_SPLIT)) {
    if (piece === "") {
      continue;
    }
    if (T9_DIGITS.test(piece)) {
      words.push(piece);
    } else {
      invalid.push(piece);
    }
  }
  return { words, invalid };
}

const RANK_LINE = /\r?\n/;
const LETTERS_ONLY = /^[a-z]+$/;

/**
 * Word → frequency rank (1 is commonest) from the NGSL file's "lemma,form,form…" lines,
 * where the Nth ordinary line is rank N. Comment lines and supplementary lines (marked
 * with a leading ~) are skipped.
 */
export function parseRanks(text: string): Map<string, number> {
  const ranks = new Map<string, number>();
  let rank = 0;
  for (const line of text.split(RANK_LINE)) {
    if (line === "" || line.startsWith("#") || line.startsWith("~")) {
      continue;
    }
    rank += 1;
    for (const form of line.split(",")) {
      if (LETTERS_ONLY.test(form) && !ranks.has(form)) {
        ranks.set(form, rank);
      }
    }
  }
  return ranks;
}

/**
 * Digit code → words that type to it, commonest first (by rank, then alphabetical). Words
 * in `ranks` are included even if `words` lacks them, since a word list may leave out
 * short function words.
 */
export function buildT9Index(
  words: Iterable<string>,
  ranks: ReadonlyMap<string, number>
): Map<string, string[]> {
  const unique = new Set<string>(ranks.keys());
  for (const word of words) {
    if (LETTERS_ONLY.test(word)) {
      unique.add(word);
    }
  }
  const index = new Map<string, string[]>();
  for (const word of unique) {
    const code = t9Code(word);
    if (code) {
      const list = index.get(code);
      if (list) {
        list.push(word);
      } else {
        index.set(code, [word]);
      }
    }
  }
  const unranked = Number.POSITIVE_INFINITY;
  for (const list of index.values()) {
    list.sort(
      (a, b) =>
        (ranks.get(a) ?? unranked) - (ranks.get(b) ?? unranked) ||
        a.localeCompare(b, "en")
    );
  }
  return index;
}
